/**
 * bb-wishlist-sync.js  v2.1  — Bara Bros
 * ─────────────────────────────────────────────────────────────────────────────
 * Syncs WishlistEngine v3 (localStorage "bb_wishlist_boards") to/from
 * customer metafield  custom.wishlist_products  (v2 boards JSON).
 *
 * Metafield schema v2:
 *   {
 *     version: 2,
 *     boards: [
 *       {
 *         id, name, created_at, updated_at,
 *         items: [{ product_id, variant_id, handle, title, image, price, added_at }]
 *       }
 *     ]
 *   }
 *
 * v1 compatibility: if the metafield is a flat array it is automatically
 * migrated into a "My Wishlist" board (no data loss) and re-synced.
 * ─────────────────────────────────────────────────────────────────────────────
 */
(function (global) {
  'use strict';

  // ── Config ──────────────────────────────────────────────────────────────────
  var PROXY_URL       = '/apps/bb-wishlist/sync';
  var DEBOUNCE_MS     = 2000;

  // ── Debug flag — set window.BB_WISHLIST_DEBUG = true in console to enable ──────
  var _debug = (global.BB_WISHLIST_DEBUG === true);

  // ── Guard: only run for logged-in customers ──────────────────────────────────
  var customer = global.BBCustomer || {};
  if (!customer.loggedIn || !customer.id) return;

  // ── Format helpers ────────────────────────────────────────────────────────────
  function numToGID(n)  { return 'gid://shopify/Product/' + n; }
  function gidToNum(g)  { return String(g || '').split('/').pop(); }
  function urlToHandle(url) { return (url || '').replace(/.*\/products\//, '').split(/[?#]/)[0]; }
  function moneyToDecimal(s) { return String(s || '').replace(/[^0-9.]/g, '') || '0.00'; }
  function decimalToMoney(s) { var n = parseFloat(s); return isNaN(n) ? '' : '$' + n.toFixed(2); }
  function tsToISO(ts) { return new Date(ts || Date.now()).toISOString(); }

  // ── Schema converters ────────────────────────────────────────────────────────

  // WishlistEngine local item → server item
  function localItemToServer(item) {
    return {
      product_id: numToGID(item.productId),
      variant_id: null,
      handle:     urlToHandle(item.url) || '',
      title:      item.title  || '',
      image:      item.image  || '',
      price:      moneyToDecimal(item.price),
      added_at:   tsToISO(item.addedAt)
    };
  }

  // Server item → WishlistEngine local item
  function serverItemToLocal(si) {
    var numId = gidToNum(si.product_id);
    return {
      id:        'srv-' + numId,
      productId: numId,
      title:     si.title || '',
      image:     si.image || '',
      url:       '/products/' + (si.handle || numId),
      price:     decimalToMoney(si.price),
      vendor:    '',
      addedAt:   si.added_at ? new Date(si.added_at).getTime() : Date.now()
    };
  }

  // ── v1/v2 normalizer ─────────────────────────────────────────────────────────
  // Always returns a v2 object regardless of input format.
  function normalizeServerData(serverData) {
    // v1: flat array — migrate into default board
    if (Array.isArray(serverData)) {
      return {
        version:  2,
        migrated: true,
        boards:   serverData.length > 0 ? [{
          id:         'my-wishlist',
          name:       (global.BBWishlistConfig && global.BBWishlistConfig.defaultBoardName) || 'My Wishlist',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          items:      serverData
        }] : []
      };
    }
    // v2: boards object
    if (serverData && serverData.version === 2 && Array.isArray(serverData.boards)) {
      return serverData;
    }
    // unknown or empty
    return { version: 2, boards: [] };
  }

  // ── Merge server boards → local ───────────────────────────────────────────────
  // Returns { merged: Number, needsSync: Boolean }
  // needsSync is true when v1 data was migrated (so the server gets upgraded).
  //
  // Board-move logic: if a product exists locally in Board A but the server
  // places it in Board B, compare added_at timestamps. When the server item is
  // newer (the move happened on another device), remove it from the stale local
  // board(s) and add it to the server board. This makes mobile reflect desktop
  // moves after a page refresh without any server round-trip.
  function mergeServerIntoLocal(WL, rawServerData) {
    if (!rawServerData) return { merged: 0, needsSync: false };

    var normalized  = normalizeServerData(rawServerData);
    var wasMigrated = normalized.migrated === true;
    var merged      = 0;

    if(_debug) console.log('[BB Wishlist Merge] Received v2 server data — boards:', normalized.boards.length);

    normalized.boards.forEach(function (serverBoard) {
      // Find or create the matching local board
      var localBoard = WL.getBoard(serverBoard.id);
      if (!localBoard) {
        var byName = WL.getBoards().filter(function (b) { return b.name === serverBoard.name; })[0];
        localBoard = byName || null;
      }
      if (!localBoard) {
        localBoard = WL.createBoard(serverBoard.name);
      }

      (serverBoard.items || []).forEach(function (si) {
        var numId = gidToNum(si.product_id);
        if (!numId) return;

        var localBoardsWithProduct = WL.getBoardsContainingProduct(numId);

        if (localBoardsWithProduct.length === 0) {
          // Not in any local board — add it
          WL.addItem(localBoard.id, serverItemToLocal(si));
          merged++;
          return;
        }

        // Product exists locally — check if it is already in the correct board
        var alreadyCorrect = localBoardsWithProduct.some(function (lb) {
          return lb.id === localBoard.id;
        });
        if (alreadyCorrect) return; // Nothing to do

        // Product is in wrong local board(s). Compare per-item timestamps:
        // WL.addItem stamps addedAt = Date.now() on every move, so the server
        // item's added_at will be newer than the stale local item's addedAt
        // whenever the move originated on another device.
        var serverTs = si.added_at ? new Date(si.added_at).getTime() : 0;
        var staleBoard = localBoardsWithProduct[0];
        var staleItem  = staleBoard.items.find(function (i) { return i.productId === numId; });
        var localTs    = staleItem ? (staleItem.addedAt || 0) : 0;

        if (serverTs >= localTs) {
          // Server placement is newer — move item to server board
          var fromName = staleBoard.name;
          localBoardsWithProduct.forEach(function (lb) {
            var oitem = lb.items.find(function (i) { return i.productId === numId; });
            if (oitem) WL.removeItem(lb.id, oitem.id);
          });
          WL.addItem(localBoard.id, serverItemToLocal(si));
          merged++;
          if(_debug) console.log('[BB Wishlist Merge] Moved stale local item from "' + fromName + '" → server board "' + serverBoard.name + '" (server ts: ' + serverTs + ', local ts: ' + localTs + ')');
        }
        // If local ts is newer, keep local placement — it will push to server on next change
      });
    });

    return { merged: merged, needsSync: wasMigrated };
  }

  // ── Collect all local boards as v2 server payload ────────────────────────────
  function collectAllForServer(WL) {
    var boards = WL.getBoards();
    return {
      version: 2,
      boards: boards.map(function (board) {
        var seen  = {};
        var items = [];
        (board.items || []).forEach(function (item) {
          var id = String(item.productId);
          if (!seen[id]) { seen[id] = true; items.push(localItemToServer(item)); }
        });
        return {
          id:         board.id,
          name:       board.name,
          created_at: tsToISO(board.createdAt),
          updated_at: tsToISO(board.updatedAt || board.createdAt),
          items:      items
        };
      })
    };
  }

  // ── Wait for WishlistEngine ───────────────────────────────────────────────────
  function waitForEngine(cb) {
    if (global.WishlistEngine && global.WishlistEngine.WL) {
      cb(global.WishlistEngine.WL); return;
    }
    var ticks = 0;
    var poll = setInterval(function () {
      if (global.WishlistEngine && global.WishlistEngine.WL) {
        clearInterval(poll); cb(global.WishlistEngine.WL);
      } else if (++ticks > 200) { clearInterval(poll); }
    }, 50);
  }

  // ── Sync status badge ─────────────────────────────────────────────────────────
  var _badgeEl   = null;
  var _hideTimer = null;

  function getBadge() {
    if (!_badgeEl) {
      _badgeEl = document.createElement('div');
      _badgeEl.id = 'bb-wl-sync-badge';
      _badgeEl.style.cssText = [
        'position:fixed','bottom:24px','left:50%',
        'transform:translateX(-50%) translateY(16px)',
        'background:rgba(5,8,22,0.96)',
        'border:1px solid rgba(0,231,255,0.22)',
        'backdrop-filter:blur(14px)','-webkit-backdrop-filter:blur(14px)',
        'color:#fff','font-size:11px','font-weight:600','letter-spacing:.5px',
        'padding:8px 20px','border-radius:20px','z-index:999999',
        'display:flex','align-items:center','gap:8px','white-space:nowrap',
        'opacity:0','transition:opacity .25s ease, transform .25s ease',
        'pointer-events:none'
      ].join(';');
      document.body.appendChild(_badgeEl);
    }
    return _badgeEl;
  }

  if (!document.getElementById('bb-wl-sync-style')) {
    var styleEl = document.createElement('style');
    styleEl.id  = 'bb-wl-sync-style';
    styleEl.textContent = '@keyframes bbWLSpin{to{transform:rotate(360deg)}}';
    document.head.appendChild(styleEl);
  }

  var STATE_CONFIG = {
    syncing: { icon: '⟳',  color: '#00E7FF', label: 'Saving wishlist…',          spin: true  },
    success: { icon: '✓',  color: '#00FF80', label: 'Wishlist saved',             spin: false },
    error:   { icon: '⚠',  color: '#FF6B6B', label: 'Saved locally (offline)',    spin: false }
  };

  function showBadge(state) {
    clearTimeout(_hideTimer);
    var el  = getBadge();
    var cfg = STATE_CONFIG[state] || STATE_CONFIG.syncing;
    var iconStyle = 'color:' + cfg.color + ';display:inline-block;' +
      (cfg.spin ? 'animation:bbWLSpin 1s linear infinite;' : '');
    el.innerHTML =
      '<span style="' + iconStyle + '">' + cfg.icon + '</span>' +
      '<span>'         + cfg.label       + '</span>';
    el.style.opacity   = '1';
    el.style.transform = 'translateX(-50%) translateY(0)';
    if (!cfg.spin) {
      _hideTimer = setTimeout(function () {
        el.style.opacity   = '0';
        el.style.transform = 'translateX(-50%) translateY(16px)';
      }, 3000);
    }
  }

  // ── POST to App Proxy ─────────────────────────────────────────────────────────
  var _debounceTimer = null;
  var _syncing       = false;
  var _pendingRetry  = false;

  function scheduleSync() {
    clearTimeout(_debounceTimer);
    _debounceTimer = setTimeout(doSync, DEBOUNCE_MS);
  }

  function doSync() {
    if (_syncing) { _pendingRetry = true; return; }

    waitForEngine(function (WL) {
      _syncing      = true;
      _pendingRetry = false;
      showBadge('syncing');

      var payload = collectAllForServer(WL); // { version: 2, boards: [...] }
      if(_debug) { var _ti = payload.boards.reduce(function(s,b){return s+b.items.length;},0); console.log('[BB Wishlist Sync] POST payload — boards:', payload.boards.length, '| items:', _ti); }

      fetch(PROXY_URL, {
        method:      'POST',
        headers:     { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        credentials: 'same-origin',
        body:        JSON.stringify(payload)
      })
        .then(function (r) {
          var ct = r.headers.get('content-type') || '';
          if (!ct.includes('application/json')) {
            return r.text().then(function (txt) {
              console.warn('[BB Wishlist Sync] Non-JSON response — status:', r.status,
                '| content-type:', ct, '| body (first 300):', txt.slice(0, 300));
              throw new Error('Non-JSON response: HTTP ' + r.status);
            });
          }
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function () {
          _syncing = false;
          showBadge('success');
          if (_pendingRetry) scheduleSync();
        })
        .catch(function (err) {
          _syncing = false;
          showBadge('error');
          console.warn('[BB Wishlist Sync] Sync failed:', err.message);
          if (_pendingRetry) scheduleSync();
        });
    });
  }

  // ── Init ──────────────────────────────────────────────────────────────────────
  waitForEngine(function (WL) {

    // Step 1: Merge server data into localStorage
    // Events fired by WL.createBoard/addItem during merge are discarded
    // because the bb:wl:changed listener isn't attached yet.
    var serverData = global.BBWishlistServerData;
    var needsSync  = false;

    if (serverData) {
      var result = mergeServerIntoLocal(WL, serverData);
      needsSync  = result.needsSync;
      if (result.merged > 0 && global.WishlistEngine) {
        global.WishlistEngine.updateAllHearts();
        global.WishlistEngine.updateWishlistCounters();
      }
    }

    // Force sync if the metafield is not yet v2 (flat array or missing version)
    if (!needsSync && (!serverData || !serverData.version || serverData.version < 2)) {
      needsSync = true;
    }

    // Step 2: Watch for future wishlist changes (listen on window per requirement 8)
    window.addEventListener('bb:wl:changed',        scheduleSync);
    document.addEventListener('bb:wl:changed',      scheduleSync);
    document.addEventListener('add:wishlist-item',  scheduleSync);
    document.addEventListener('remove:wishlist-item', scheduleSync);

    // Step 3: If metafield is v1 or missing v2, push upgraded structure now
    if (needsSync) scheduleSync();
  });

})(window);
